import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { 
  Code, 
  Play, 
  Copy, 
  CheckCircle, 
  ChevronDown,
  ChevronRight,
  Globe,
  Shield,
  Zap
} from 'lucide-react'

const APIDocs: React.FC = () => {
  const [copiedCode, setCopiedCode] = useState('')
  const [expandedEndpoint, setExpandedEndpoint] = useState<string | null>(null)

  const copyToClipboard = (code: string, id: string) => {
    navigator.clipboard.writeText(code)
    setCopiedCode(id)
    setTimeout(() => setCopiedCode(''), 2000)
  }

  const toggleEndpoint = (endpoint: string) => {
    setExpandedEndpoint(expandedEndpoint === endpoint ? null : endpoint)
  }

  const endpoints = [
    {
      method: 'GET',
      path: '/api/news',
      title: '获取新闻列表',
      description: '获取新闻文章列表，支持分页和筛选',
      category: 'news',
      parameters: [
        { name: 'page', type: 'number', required: false, description: '页码，默认为1' },
        { name: 'limit', type: 'number', required: false, description: '每页数量，默认为10' },
        { name: 'category', type: 'string', required: false, description: '新闻分类' },
        { name: 'search', type: 'string', required: false, description: '搜索关键词' }
      ],
      response: {
        success: true,
        data: [
          {
            _id: 'string',
            title: 'string',
            excerpt: 'string',
            author: 'string',
            category: 'string',
            publishedAt: 'string',
            views: 'number'
          }
        ],
        pagination: {
          page: 'number',
          pages: 'number',
          total: 'number',
          hasNext: 'boolean',
          hasPrev: 'boolean'
        }
      },
      example: `curl -X GET "https://api.deeplink.com/api/news?page=1&limit=10&category=产品发布" \\
  -H "Content-Type: application/json"`
    },
    {
      method: 'POST',
      path: '/api/news',
      title: '创建新闻',
      description: '创建新的新闻文章（需要管理员权限）',
      category: 'news',
      parameters: [
        { name: 'title', type: 'string', required: true, description: '新闻标题' },
        { name: 'content', type: 'string', required: true, description: '新闻内容' },
        { name: 'author', type: 'string', required: true, description: '作者' },
        { name: 'category', type: 'string', required: true, description: '新闻分类' },
        { name: 'image', type: 'string', required: true, description: '新闻图片URL' }
      ],
      response: {
        success: true,
        data: {
          _id: 'string',
          title: 'string',
          content: 'string',
          author: 'string',
          category: 'string',
          publishedAt: 'string'
        },
        message: '新闻创建成功'
      },
      example: `curl -X POST "https://api.deeplink.com/api/news" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -d '{
    "title": "CoreTrust手机正式发布",
    "content": "详细内容...",
    "author": "DeepLink团队",
    "category": "产品发布",
    "image": "https://example.com/image.jpg"
  }'`
    },
    {
      method: 'GET',
      path: '/api/careers',
      title: '获取招聘职位',
      description: '获取招聘职位列表，支持筛选和搜索',
      category: 'careers',
      parameters: [
        { name: 'department', type: 'string', required: false, description: '部门筛选' },
        { name: 'location', type: 'string', required: false, description: '地点筛选' },
        { name: 'type', type: 'string', required: false, description: '职位类型' }
      ],
      response: {
        success: true,
        data: [
          {
            _id: 'string',
            title: 'string',
            department: 'string',
            location: 'string',
            type: 'string',
            description: 'string',
            requirements: ['string'],
            benefits: ['string']
          }
        ]
      },
      example: `curl -X GET "https://api.deeplink.com/api/careers?department=技术部&type=全职" \\
  -H "Content-Type: application/json"`
    },
    {
      method: 'POST',
      path: '/api/contact/submit',
      title: '提交联系表单',
      description: '提交联系表单或咨询信息',
      category: 'contact',
      parameters: [
        { name: 'name', type: 'string', required: true, description: '姓名' },
        { name: 'email', type: 'string', required: true, description: '邮箱' },
        { name: 'phone', type: 'string', required: false, description: '电话' },
        { name: 'subject', type: 'string', required: true, description: '主题' },
        { name: 'message', type: 'string', required: true, description: '消息内容' }
      ],
      response: {
        success: true,
        message: '消息提交成功，我们会尽快回复您'
      },
      example: `curl -X POST "https://api.deeplink.com/api/contact/submit" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "张三",
    "email": "zhangsan@example.com",
    "phone": "13800138000",
    "subject": "产品咨询",
    "message": "我想了解CoreTrust手机的详细信息"
  }'`
    }
  ]

  const categories = [
    { id: 'all', name: '全部', count: endpoints.length },
    { id: 'news', name: '新闻', count: endpoints.filter(e => e.category === 'news').length },
    { id: 'careers', name: '招聘', count: endpoints.filter(e => e.category === 'careers').length },
    { id: 'contact', name: '联系', count: endpoints.filter(e => e.category === 'contact').length }
  ]

  const [selectedCategory, setSelectedCategory] = useState('all')

  const filteredEndpoints = endpoints.filter(endpoint => 
    selectedCategory === 'all' || endpoint.category === selectedCategory
  )

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'GET': return 'from-green-500 to-emerald-500'
      case 'POST': return 'from-blue-500 to-cyan-500'
      case 'PUT': return 'from-yellow-500 to-orange-500'
      case 'DELETE': return 'from-red-500 to-pink-500'
      default: return 'from-gray-500 to-gray-600'
    }
  }

  return (
    <>
      <Helmet>
        <title>API文档 - DeepLink Limited</title>
        <meta name="description" content="DeepLink Limited API文档，包含完整的接口说明、参数和示例代码。" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <section className="py-20 bg-gradient-to-r from-purple-600 to-pink-600">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="w-24 h-24 bg-white/20 rounded-3xl flex items-center justify-center mx-auto mb-8">
                <Code className="w-12 h-12 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                API文档
              </h1>
              <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
                完整的API接口文档，支持RESTful设计
              </p>
              
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 max-w-2xl mx-auto">
                <div className="flex items-center justify-center mb-4">
                  <Globe className="w-5 h-5 text-white mr-2" />
                  <span className="text-white font-semibold">API基础URL</span>
                </div>
                <code className="text-white text-lg font-mono">
                  https://api.deeplink.com
                </code>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-8 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap gap-4 justify-center">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                    selectedCategory === category.id
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {category.name} ({category.count})
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* API Endpoints */}
        <section className="py-20">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              {filteredEndpoints.map((endpoint, index) => (
                <motion.div
                  key={`${endpoint.method}-${endpoint.path}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden"
                >
                  <div 
                    className="p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => toggleEndpoint(`${endpoint.method}-${endpoint.path}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`w-16 h-8 bg-gradient-to-r ${getMethodColor(endpoint.method)} rounded-lg flex items-center justify-center`}>
                          <span className="text-white font-bold text-sm">{endpoint.method}</span>
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                            {endpoint.title}
                          </h3>
                          <p className="text-gray-600 dark:text-gray-400">
                            {endpoint.path}
                          </p>
                        </div>
                      </div>
                      {expandedEndpoint === `${endpoint.method}-${endpoint.path}` ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mt-4">
                      {endpoint.description}
                    </p>
                  </div>

                  {expandedEndpoint === `${endpoint.method}-${endpoint.path}` && (
                    <div className="border-t border-gray-200 dark:border-gray-700 p-6">
                      {/* Parameters */}
                      <div className="mb-8">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                          参数
                        </h4>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-gray-200 dark:border-gray-700">
                                <th className="text-left py-2 px-4 font-semibold text-gray-900 dark:text-white">参数名</th>
                                <th className="text-left py-2 px-4 font-semibold text-gray-900 dark:text-white">类型</th>
                                <th className="text-left py-2 px-4 font-semibold text-gray-900 dark:text-white">必需</th>
                                <th className="text-left py-2 px-4 font-semibold text-gray-900 dark:text-white">描述</th>
                              </tr>
                            </thead>
                            <tbody>
                              {endpoint.parameters.map((param, paramIndex) => (
                                <tr key={paramIndex} className="border-b border-gray-100 dark:border-gray-700">
                                  <td className="py-2 px-4 font-mono text-sm text-gray-900 dark:text-white">{param.name}</td>
                                  <td className="py-2 px-4 text-gray-600 dark:text-gray-400">{param.type}</td>
                                  <td className="py-2 px-4">
                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                      param.required 
                                        ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' 
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                    }`}>
                                      {param.required ? '是' : '否'}
                                    </span>
                                  </td>
                                  <td className="py-2 px-4 text-gray-600 dark:text-gray-400">{param.description}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Response */}
                      <div className="mb-8">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                          响应示例
                        </h4>
                        <div className="relative">
                          <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                            <pre className="text-green-400 text-sm">
                              <code>{JSON.stringify(endpoint.response, null, 2)}</code>
                            </pre>
                          </div>
                          <button
                            onClick={() => copyToClipboard(JSON.stringify(endpoint.response, null, 2), `response-${index}`)}
                            className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                          >
                            {copiedCode === `response-${index}` ? (
                              <CheckCircle className="w-4 h-4 text-green-400" />
                            ) : (
                              <Copy className="w-4 h-4 text-gray-300" />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Example */}
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                          请求示例
                        </h4>
                        <div className="relative">
                          <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                            <pre className="text-blue-400 text-sm">
                              <code>{endpoint.example}</code>
                            </pre>
                          </div>
                          <button
                            onClick={() => copyToClipboard(endpoint.example, `example-${index}`)}
                            className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                          >
                            {copiedCode === `example-${index}` ? (
                              <CheckCircle className="w-4 h-4 text-green-400" />
                            ) : (
                              <Copy className="w-4 h-4 text-gray-300" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* API Info */}
        <section className="py-20 bg-white dark:bg-gray-800">
          <div className="container mx-auto px-4 max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                API特性
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  安全可靠
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  采用HTTPS加密传输，支持API密钥认证，确保数据安全
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  高性能
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  响应时间小于100ms，支持高并发访问，99.9%可用性保证
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Code className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  易于集成
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  提供多语言SDK，详细的文档和示例，快速集成到您的应用
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default APIDocs